import { useEffect, useRef } from 'react';

/**
 * Refetch-on-navigation: calls `refetch` when the tab/window becomes active
 * again — either the document turns visible (visibilitychange) or the window
 * regains focus. This keeps a mounted dashboard fresh without Realtime
 * subscriptions: switch away and back, or act in another tab, and the data
 * re-pulls on return.
 *
 * `refetch` is stored in a ref so the listeners always call the latest closure
 * without re-binding on every render. Pair it with an in-flight guard inside the
 * fetch itself (so a focus + visibility double-fire, or an overlapping mount
 * fetch, collapses to one request).
 *
 * @param {() => void} refetch  invoked on focus / visibility-visible
 * @param {{ enabled?: boolean }} [opts]  set enabled:false to detach listeners
 */
export function useRefetchOnActive(refetch, { enabled = true } = {}) {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!enabled) return undefined;

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchRef.current?.();
    };
    const onFocus = () => refetchRef.current?.();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled]);
}
